import type { RatingExport } from '../models';
import type { Demographics } from '../../shared/config.schema';
import { getOrCreateUserUUID } from './storage';

function getTokenCookie(): string {
  const match = document.cookie.match(/(?:^|;\s*)_token=([^;]+)/);
  return match?.[1] ?? '';
}

/**
 * The beta subdomain is served from the same static bundle but has its API
 * traffic routed separately (e.g. to a different backend), so the client
 * must pick the matching path prefix at runtime rather than hardcoding it.
 */
export function getApiPrefix(hostname: string = window.location.hostname): string {
  return hostname.startsWith('beta-api.') ? '/beta-api' : '/api';
}

/**
 * Thrown by `_post` when the server responds with a non-2xx status. Carries
 * the HTTP status and the parsed JSON error body (when the response had
 * one) so callers can react to specific failure modes (e.g. 429 rate
 * limiting) instead of treating every failure identically.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    path: string,
  ) {
    super(`POST ${path} failed: ${status}`);
    this.name = 'ApiError';
  }
}

/**
 * Reads the `retryAfter` (seconds) field out of a 429 error body, if present.
 */
export function getRetryAfterSeconds(body: unknown): number | null {
  if (typeof body !== 'object' || body === null) return null;
  const retryAfter = (body as Record<string, unknown>)['retryAfter'];
  return typeof retryAfter === 'number' ? retryAfter : null;
}

// body is typed as unknown rather than Record<string, unknown> so TypeScript
// treats the guards below as reachable; a narrower type would make them dead
// code in strict mode, silently removing the runtime defence.
export async function _post<T>(path: string, body: unknown): Promise<T> {
  if (typeof body !== 'object' || body === null || Array.isArray(body)) {
    throw new TypeError('post: body must be a plain object');
  }
  const resolved = new URL(path, window.location.origin);
  if (resolved.origin !== window.location.origin) {
    throw new Error('post: cross-origin requests are not allowed');
  }
  const url = resolved.href;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getTokenCookie() },
    body: JSON.stringify(body as Record<string, unknown>),
  });
  if (!res.ok) {
    const errorBody = await res.json().catch(() => null);
    throw new ApiError(res.status, errorBody, path);
  }
  return res.json() as Promise<T>;
}

export async function checkHealth(): Promise<boolean> {
  try {
    const res = await fetch(`${getApiPrefix()}/health`);
    if (!res.ok) return false;
    const data = (await res.json()) as unknown;
    return typeof data === 'object' && data !== null && (data as Record<string, unknown>).ok === true;
  } catch {
    return false;
  }
}

export async function submitRatings(
  ratings: RatingExport[],
  demographics?: Demographics,
): Promise<{ ids: number[]; submissionUuids: string[]; submitterId: string }> {
  const submitterId = getOrCreateUserUUID();
  const body: Record<string, unknown> = { ratings, submitterId };
  if (demographics) body['demographics'] = demographics;

  return _post<{ ids: number[]; submissionUuids: string[]; submitterId: string }>(
    `${getApiPrefix()}/submitRatings`,
    body,
  );
}
